import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Shield, ShieldUser, ShieldHalf, CheckSquare, Square } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/Common";
import { toast } from "sonner";
import { MENU_ITEMS, DEFAULT_PERMISSIONS } from "@/constants/menus";
import { useAuth } from "@/contexts/AuthContext";

const ROLE_META = {
  admin: { label: "Administrador", icon: Shield, color: "#2A3F35" },
  vendedor: { label: "Vendedor", icon: ShieldUser, color: "#608090" },
  bodeguero: { label: "Bodeguero", icon: ShieldHalf, color: "#84978F" },
};

const EMPTY = { email: "", name: "", password: "", role: "vendedor",
                permissions: DEFAULT_PERMISSIONS.vendedor };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const { user: currentUser } = useAuth();

  const load = () => api.get("/auth/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY, permissions: DEFAULT_PERMISSIONS.vendedor });
    setShowForm(true);
  };

  const openEdit = (u) => {
    setEditingId(u.id);
    setForm({
      email: u.email, name: u.name, password: "",
      role: u.role, permissions: u.permissions || [],
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const payload = {
          name: form.name, role: form.role, permissions: form.permissions,
        };
        if (form.password) payload.password = form.password;
        await api.put(`/auth/users/${editingId}`, payload);
        toast.success("Usuario actualizado");
      } else {
        await api.post("/auth/users", form);
        toast.success("Usuario creado");
      }
      setShowForm(false); setEditingId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const onDelete = async (u) => {
    if (!window.confirm(`¿Eliminar al usuario ${u.name} (${u.email})? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/auth/users/${u.id}`);
      toast.success("Usuario eliminado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const togglePerm = (key) => {
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(key)
        ? f.permissions.filter((k) => k !== key)
        : [...f.permissions, key],
    }));
  };

  const applyRolePreset = (role) => {
    setForm((f) => ({ ...f, role, permissions: DEFAULT_PERMISSIONS[role] || [] }));
  };

  return (
    <div data-testid="users-page">
      <PageHeader
        title="Usuarios"
        subtitle="Gestión de usuarios, roles y permisos por menú."
        testid="users-header"
        action={
          <button data-testid="users-create-button" onClick={openCreate} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nuevo usuario
          </button>
        }
      />

      {showForm && (
        <div data-testid="user-form" className="card-soft p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-xl font-bold">{editingId ? "Editar" : "Nuevo"} usuario</h3>
            <button onClick={() => setShowForm(false)} className="text-[#8A9A92] hover:text-[#2A3F35]"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={submit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Nombre completo *">
                <input data-testid="user-name" required className="input-soft" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </Field>
              <Field label="Email *">
                <input data-testid="user-email" type="email" required disabled={!!editingId} className="input-soft disabled:opacity-60" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </Field>
              <Field label={editingId ? "Contraseña (dejar vacío para no cambiar)" : "Contraseña * (mín. 6)"}>
                <input data-testid="user-password" type="password" minLength={editingId ? 0 : 6} required={!editingId} className="input-soft"
                  value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </Field>
              <label className="block">
                <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Rol *</span>
                <select data-testid="user-role" className="input-soft" value={form.role}
                  onChange={(e) => applyRolePreset(e.target.value)}>
                  <option value="admin">Administrador (acceso total)</option>
                  <option value="vendedor">Vendedor</option>
                  <option value="bodeguero">Bodeguero</option>
                </select>
              </label>
            </div>

            {form.role !== "admin" && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs uppercase tracking-[0.18em] text-[#8A9A92]">Menús visibles para este usuario</p>
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setForm({ ...form, permissions: MENU_ITEMS.filter((m) => !m.adminOnly).map((m) => m.key) })} className="text-xs text-[#608090] hover:underline">Seleccionar todos</button>
                    <span className="text-[#E2E0D8]">·</span>
                    <button type="button" onClick={() => setForm({ ...form, permissions: [] })} className="text-xs text-[#A44A4A] hover:underline">Limpiar</button>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="user-permissions-grid">
                  {MENU_ITEMS.filter((m) => !m.adminOnly).map((m) => {
                    const checked = form.permissions.includes(m.key);
                    return (
                      <button key={m.key} type="button" onClick={() => togglePerm(m.key)}
                        data-testid={`perm-${m.key}`}
                        className={`flex items-center gap-2 p-3 rounded-lg border text-left transition ${
                          checked ? "border-[#2A3F35] bg-[#E4EFE6]" : "border-[#E2E0D8] hover:border-[#84978F]"
                        }`}>
                        {checked ? <CheckSquare className="w-4 h-4 text-[#2A3F35]" /> : <Square className="w-4 h-4 text-[#8A9A92]" />}
                        <span className="text-sm">{m.label}</span>
                      </button>
                    );
                  })}
                </div>
                {form.permissions.length === 0 && (
                  <p className="mt-2 text-xs text-[#A44A4A]">⚠ Sin menús seleccionados, el usuario no podrá ver ninguna sección al iniciar sesión.</p>
                )}
              </div>
            )}
            {form.role === "admin" && (
              <p className="text-sm text-[#5C6B64] bg-[#F3F2ED] p-3 rounded-md">
                Los administradores tienen acceso a todos los menús del sistema, independientemente de los permisos asignados.
              </p>
            )}

            <div className="flex justify-end gap-3 pt-2 border-t border-[#E2E0D8]">
              <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
              <button data-testid="user-save-button" className="btn-primary" type="submit">
                {editingId ? "Guardar cambios" : "Crear usuario"}
              </button>
            </div>
          </form>
        </div>
      )}

      {users.length === 0 ? (
        <EmptyState title="Cargando..." hint="" testid="users-empty" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="users-grid">
          {users.map((u) => {
            const meta = ROLE_META[u.role] || ROLE_META.vendedor;
            const Icon = meta.icon;
            const isCurrent = currentUser?.id === u.id;
            return (
              <div key={u.id} className="card-soft p-5" data-testid={`user-card-${u.email}`}>
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-11 h-11 rounded-xl grid place-items-center shrink-0" style={{ background: `${meta.color}22` }}>
                    <Icon className="w-5 h-5" strokeWidth={1.7} style={{ color: meta.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-bold text-[#2A3F35] truncate">
                      {u.name}{isCurrent && <span className="text-[10px] uppercase tracking-wider text-[#608090] ml-2">(tú)</span>}
                    </p>
                    <p className="text-sm text-[#5C6B64] truncate">{u.email}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between mb-3">
                  <span className="status-chip" style={{ background: `${meta.color}1f`, color: meta.color }}>{meta.label}</span>
                  <span className={`text-xs ${u.active ? "text-[#507A59]" : "text-[#A44A4A]"}`}>
                    {u.active ? "● Activo" : "● Inactivo"}
                  </span>
                </div>
                {u.role !== "admin" && (
                  <p className="text-xs text-[#8A9A92] mb-3">
                    {(u.permissions || []).length} menú(s) asignados
                  </p>
                )}
                <div className="flex gap-2 pt-3 border-t border-[#E2E0D8]">
                  <button data-testid={`user-edit-${u.email}`} onClick={() => openEdit(u)} className="flex-1 btn-secondary text-sm flex items-center justify-center gap-2">
                    <Pencil className="w-3.5 h-3.5" /> Editar
                  </button>
                  <button data-testid={`user-delete-${u.email}`} onClick={() => onDelete(u)} disabled={isCurrent}
                    className="px-3 py-2 rounded-lg border border-[#F3DADA] text-[#A44A4A] hover:bg-[#F3DADA] disabled:opacity-40 disabled:cursor-not-allowed">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
